import Icon from '@/components/icons/Icon';
import type { ReactHookFormRegister } from '@/components/modules/checkout/CheckoutFormFields/CheckoutFormFields';
import { classnames } from '@/helpers';
import { InputHTMLAttributes, LegacyRef, forwardRef, useId, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { twMerge } from 'tailwind-merge';

type GenericListInputProps = {
  id: string;
  label?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  mask: string;
  maskPlaceholder?: string;
  floatStyle?: boolean;
  setValue?: UseFormSetValue<any>;
  inputValue?: string;
};

type ListInputProps = InputHTMLAttributes<HTMLInputElement> & GenericListInputProps & ReactHookFormRegister<any>;

const ListMaskedInput = forwardRef(
  (
    {
      id,
      label,
      mask,
      maskPlaceholder,
      error,
      leftIcon,
      type = 'text',
      className,
      disabled,
      floatStyle = false,
      inputValue = '',
      setValue, // Destructure setValue
      onBlur: onBlurFromProps, // Rename onBlur prop
      ...props
    }: ListInputProps,
    ref: LegacyRef<HTMLInputElement>,
  ) => {
    // Minted rather than taken from `id`: callers reuse field ids across surfaces, and
    // `htmlFor` binds to whichever element carries that id first in tree order.
    const inputId = useId();
    const [isFocused, setIsFocused] = useState(false);
    const showClearButton =
      isFocused &&
      inputValue !== undefined &&
      inputValue !== '' &&
      inputValue != maskPlaceholder &&
      !disabled &&
      !error;

    const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (onBlurFromProps) {
        onBlurFromProps(event); // Call the original onBlur handler from props
      }
    };

    const handleOnClear = () => {
      if (setValue) {
        setValue(id, '');
        setIsFocused(true);
      }
    };

    if (floatStyle) {
      return (
        <div className={classnames('pl-lg pr-lg flex w-full flex-col', disabled && 'opacity-40')}>
          <div
            className={classnames(
              'px-md relative flex items-center gap-2 rounded-sm border py-2',
              error ? 'border-danger-500' : 'border-black-100',
              'float-group',
            )}>
            {leftIcon && <div className="w-7">{leftIcon}</div>}
            <div className="relative w-full">
              {label && (
                <label
                  htmlFor={inputId}
                  className={classnames(
                    'text-black-600 text-l absolute left-0 top-2 block',
                    disabled && 'text-black-400',
                  )}>
                  {label}
                </label>
              )}
              <InputMask
                id={inputId}
                mask={mask}
                maskPlaceholder={maskPlaceholder}
                ref={ref}
                type={type}
                className={twMerge(
                  'text-black-900 text-l peer mt-2 w-full placeholder-transparent outline-none disabled:bg-transparent',
                  className,
                )}
                disabled={disabled}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                {...props}
              />
            </div>
            {showClearButton && (
              <Icon
                className="clear-button absolute right-2 top-4 cursor-pointer"
                variant="close-circle"
                color="black-600"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleOnClear}
              />
            )}
          </div>
          {error && <div className="text-danger text-xxs mt-xxs flex justify-start">{error}</div>}
        </div>
      );
    }

    return (
      <div className={classnames('pl-lg flex w-full flex-col', disabled && 'opacity-40')}>
        {label && (
          <label htmlFor={inputId} className="text-black-900 pt-md text-m block">
            {label}
          </label>
        )}
        <div className={classnames('pr-lg relative flex border-b', error ? 'border-danger-500' : 'border-black-100')}>
          {leftIcon && <div className="pr-md mt-md">{leftIcon}</div>}
          <InputMask
            id={inputId}
            mask={mask}
            ref={ref}
            type={type}
            className={twMerge(
              'placeholder:text-black-600 text-black-900 py-md w-full text-sm outline-none disabled:bg-transparent',
              className,
            )}
            disabled={disabled}
            {...props}
          />
          {error && (
            <div className="pr-md mt-md">
              <Icon variant="warning" color="danger-500" />
            </div>
          )}
        </div>
        {error && <div className="text-danger text-xxs mt-xxs flex justify-start">{error}</div>}
      </div>
    );
  },
);

export default ListMaskedInput;
